<template>
    <div>
        <limitless-page-header
            title="Kortbeställning"
            :sub-title="'E-kort - Ordernummer: ' + this.id"
            :title-template="'E-kort - Kortbeställning #' + this.id"
        ></limitless-page-header>

        <div class="content">
            <tb-skeleton v-if="busy_loading" :height="100" :theme="'opacity'" :shape="'radius'" bg-color="#dcdbdc"></tb-skeleton>
            <div v-else-if="settings">
                <div class="ecard-savebar card p-3 mb-3" role="region" aria-label="Spara e-kortinställningar">
                    <div class="d-flex flex-wrap align-items-center justify-content-between">
                        <div class="mr-3" aria-live="polite">
                            <span v-if="busy_saving"><i class="icon-spinner2 spinner mr-1"></i>Sparar ändringar...</span>
                            <span v-else-if="hasUnsavedChanges" class="text-warning font-weight-semibold">Osparade ändringar</span>
                            <span v-else class="text-success">Alla ändringar är sparade</span>
                            <div v-if="hasUnsavedChanges" class="text-muted small">Spara ändringarna före utskick eller schemaläggning.</div>
                            <div v-if="saveError" class="text-danger" role="alert">{{ saveError }}</div>
                        </div>
                        <limitless-button ref="btnsavesettings" @click="saveTemplateSettings" :busy="busy_saving" :disabled="!hasUnsavedChanges" icon="icon-floppy-disk">Spara ändringar</limitless-button>
                    </div>
                </div>
                <div v-if="missingTemplateMessage" class="alert alert-warning" role="status">{{ missingTemplateMessage }}</div>
                <ValidationObserver ref="settingsform">
                    <limitless-tabs v-model="activeTab" :tabs="tabs" keep-mounted>
                        <template #recipients>
                            <rednax-datatable
                                ref="recipientstable"
                                no-wrapper
                                :columns="table_columns"
                                :filter="table_filter"
                                url="/vue/ecard/recipients"
                            >
                                <template #header>
                                    <h5 class="card-title">Mottagare</h5>
                                    <div class="header-elements">
                                        <button type="button" class="btn btn-light ml-2" @click="$bvModal.show('modal-edit-date')"
                                            :disabled="sendBlocked" :title="sendBlocked ? 'Spara ändringarna före utskick eller schemaläggning' : ''">
                                            <i class="icon-alarm mr-1"></i>Schemalägg/Ändra utskicksdatum
                                        </button>
                                        <button type="button" class="btn btn-light ml-2" @click="$bvModal.show('modal-add-recipients')"><i class="icon-plus3 mr-1"></i>Lägg till mottagare</button>
                                    </div>
                                </template>
                                <template #default>
                                    <div class="row">
                                        <div class="col-lg-3">
                                            <input type="text" class="form-control" v-model="table_filter.search" placeholder="Sök mottagare eller kortnummer..." aria-label="Sök mottagare eller kortnummer">
                                        </div>
                                        <div class="col-lg-3">
                                            <vue-select
                                                placeholder="Inlösta/Ej inlösta kort"
                                                v-model="table_filter.is_redeemed"
                                                :options="[
                                                    {
                                                        id: 1,
                                                        label: 'Inlösta kort',
                                                    },
                                                    {
                                                        id: 0,
                                                        label: 'Ej inlösta kort',
                                                    },
                                                ]"
                                                :reduce="(opt) => opt.id"
                                            ></vue-select>
                                        </div>
                                        <div class="col-lg-3">
                                            <vue-select
                                                placeholder="Kortserie"
                                                v-model="table_filter.card_series_id"
                                                :options="settings.card_series_ids"
                                            ></vue-select>
                                        </div>
                                    </div>
                                </template>
                                <template #cell_recipient_lastname="{ row }">
                                    <div class="font-weight-semibold">{{ recipientName(row) }}</div>
                                    <div v-if="recipientContact(row) && recipientContact(row) !== recipientName(row)" class="text-muted small">{{ recipientContact(row) }}</div>
                                </template>
                                <template #cell_delivery_channel="{ row }">
                                    <span class="badge badge-light">{{ row.delivery_channel === 'sms' ? 'SMS' : 'E-post' }}</span>
                                </template>
                                <template #cell_is_sent="{ row }">
                                    <span :class="'badge badge-' + recipientStatus(row).variant">{{ recipientStatus(row).label }}</span>
                                    <span v-if="Number(row.test_card_mode)" class="badge badge-warning ml-1">Testläge</span>
                                </template>
                                <template v-for="event in recipientEventColumns" v-slot:[`cell_${event}`]="{ row }">
                                    <recipient-event :key="event" :row="row" :event="event" />
                                </template>
                                <template #cell_last_sent_ts="{ row }">
                                    <span class="text-nowrap">{{ row.last_sent_ts ? formatDateTime(row.last_sent_ts) : '–' }}</span>
                                </template>
                                <template #cell_scheduled_ts="{ row }">{{ row.scheduled_ts ? formatDateTime(row.scheduled_ts) : 'Ej schemalagt' }}</template>
                                <template #cell_buttons="{ row }">
                                    <div class="d-flex flex-wrap align-items-center ecard-row-actions">
                                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="previewRecipient(row)">
                                            <i class="icon-eye mr-1"></i>Visa e-post
                                        </button>
                                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="previewRecipientSms(row)" :disabled="busySmsRecipient">
                                            <i class="icon-mobile mr-1"></i>Visa SMS
                                        </button>
                                        <span :title="!channelConfigured(row) ? 'Mall saknas för mottagarens leveranssätt' : (sendBlocked ? 'Spara ändringarna före utskick eller schemaläggning' : '')">
                                            <button type="button" class="btn btn-primary btn-sm" @click="sendRecipient(row)" :disabled="sendBlocked || !channelConfigured(row) || sendingRecipientId !== null">
                                                <i :class="sendingRecipientId === row.recipient_id ? 'icon-spinner2 spinner mr-1' : 'icon-paperplane mr-1'"></i>{{ sendLabel(row) }}
                                            </button>
                                        </span>
                                        <b-dropdown size="sm" variant="light" text="Åtgärder" right boundary="viewport">
                                            <b-dropdown-item @click="showRecipientDetails(row)">Detaljer</b-dropdown-item>
                                            <b-dropdown-item @click="editRecipient(row)">Ändra mottagare</b-dropdown-item>
                                            <b-dropdown-item @click="toggleTestmode(row)">{{ Number(row.test_card_mode) ? 'Avaktivera testläge' : 'Aktivera testläge' }}</b-dropdown-item>
                                        </b-dropdown>
                                    </div>
                                </template>
                            </rednax-datatable>
                        </template>
                        <template #sms>
                            <form @submit.prevent="saveTemplateSettings">
                                <div class="">
                                    <div class="card-header header-elements-inline bg-white">
                                        <h5 class="card-title">SMS-inställningar</h5>
                                        <div class="header-elements">
                                            <button type="button" class="btn btn-light" @click="openTestSend('sms')" :disabled="sendBlocked || !channelConfigured({ delivery_channel: 'sms' })">
                                                <i class="icon-paperplane mr-1"></i>Skicka test
                                            </button>
                                        </div>
                                    </div>
                                    <div class="card-body">
                                        <div class="row">
                                            <div class="col-lg-6 mb-3 mb-lg-0">
                                                <ValidationProvider vid="sms_text" name="SMS-text" rules="max:10000" v-slot="{ errors }">
                                                    <div class="form-group">
                                                        <label for="ecard-sms-text">SMS-text</label>
                                                        <textarea id="ecard-sms-text" ref="smsText" class="form-control" rows="5" v-model="settings.sms_text"></textarea>
                                                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </div>
                                                </ValidationProvider>
                                                <div class="d-flex flex-wrap mb-2">
                                                    <button
                                                        type="button"
                                                        class="btn btn-light mr-2 mb-2"
                                                        @click="settings.sms_text = settings.default_sms_text"
                                                        :disabled="settings.default_sms_text == null || settings.sms_text === settings.default_sms_text"
                                                    >Återställ standardtext</button>
                                                    <button type="button" class="btn btn-light mr-2 mb-2" @click="insertSmsText('https://ordergift.se/##CardNo##')">Lägg till länk för inlösen</button>
                                                    <button type="button" class="btn btn-light mr-2 mb-2" @click="insertSmsText('https://ordergift.se/ecard/view/##CardNo##')">Lägg till länk till e-postmallen</button>
                                                    <select class="form-control w-auto mb-2" aria-label="Lägg till SMS-variabel" @change="insertSmsText($event.target.value); $event.target.value = ''">
                                                        <option value="">Lägg till variabel...</option>
                                                        <option v-for="variable in smsVariables" :key="variable" :value="'##' + variable + '##'">{{ variable }}</option>
                                                    </select>
                                                </div>
                                                <small class="text-muted">Texten används för mottagare med leveranssätt SMS. Spara inställningarna före utskick. Variabler ersätts med mottagarens uppgifter.</small>
                                                <small class="text-muted d-block">Bildvariabler, även ImageSlotElement1 och ImageSlotElement2, ersätts med bildens URL i SMS.</small>
                                            </div>
                                            <div class="col-lg-6">
                                                <div class="mb-3">
                                                    <label>Exempel med påhittat kortnummer</label>
                                                    <p class="text-muted small">Exempelmottagare: Ture Testare, kortnummer 1234123412ABC</p>
                                                    <div v-if="busySmsPreview" class="text-muted" role="status">Uppdaterar förhandsgranskningen...</div>
                                                    <div v-else-if="smsPreviewError" class="text-danger" role="alert">{{ smsPreviewError }}</div>
                                                    <div v-else-if="smsPreview !== null" class="border rounded bg-light p-3 mb-2" style="white-space: pre-wrap; overflow-wrap: anywhere;">{{ smsPreview.text }}</div>
                                                    <p v-else class="text-muted">Skriv en SMS-text för att förhandsgranska.</p>
                                                    <sms-summary v-if="smsPreview !== null" :text="smsPreview.text" />
                                                    <small class="text-muted">
                                                        Förhandsgranskningen och räknaren använder aktuell SMS-text med exempelmottagarens uppgifter och ett påhittat kortnummer.
                                                        Ingen sparning behövs för att förhandsgranska.
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </template>
                        <template #email>
                            <form @submit.prevent="saveTemplateSettings">
                                <div v-if="settings.use_view">
                                    <!-- Use new templates -->
                                    <template-editor
                                        v-model="settings.view"
                                        :customer-id="settings.customer_id"
                                        :card-order-id="settings.card_order_id"
                                        mode="normal"
                                    >
                                        <div class="card">
                                            <div class="card-header header-elements-inline bg-white">
                                                <h5 class="card-title">E-kortinställningar</h5>
                                                <div class="header-elements">
                                                    <button type="button" class="btn btn-light" @click="openTestSend('email')" :disabled="sendBlocked || !channelConfigured({ delivery_channel: 'email' })">
                                                        <i class="icon-paperplane mr-1"></i>Skicka test
                                                    </button>
                                                </div>
                                            </div>
                                            <div class="card-body">
                                                <ValidationProvider vid="selection" name="Malltyp" rules="" v-slot="{ errors }">
                                                    <div class="form-group">
                                                        <label class="d-block">Malltyp</label>
                                                        <div class="custom-control custom-radio custom-control-inline">
                                                            <input type="radio" class="custom-control-input" name="use_view" id="rdo-use-view" v-model="settings.use_view" :value="1">
                                                            <label class="custom-control-label" for="rdo-use-view">Rednax™ Builder</label>
                                                        </div>
                                                        <div class="custom-control custom-radio custom-control-inline">
                                                            <input type="radio" class="custom-control-input" name="use_view" id="rdo-use-legacy" v-model="settings.use_view" :value="0">
                                                            <label class="custom-control-label" for="rdo-use-legacy">Legacy</label>
                                                        </div>
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </div>
                                                </ValidationProvider>
                                                <input-with-validation
                                                    vid="sender_name"
                                                    label="Avsändare"
                                                    v-model="settings.sender_name"
                                                    placeholder="Supékort"
                                                    rules="required"
                                                ></input-with-validation>
                                                <input-with-validation
                                                    vid="subject"
                                                    label="Ämne"
                                                    v-model="settings.subject"
                                                    rules="required"
                                                ></input-with-validation>
                                            </div>
                                        </div>
                                    </template-editor>
                                </div>
                                <div
                                    v-else
                                    class="ecard-settings-section"
                                >
                                    <!-- Use old templates -->
                                    <div class="card-header bg-white">
                                        <h5 class="card-title">E-kortinställningar</h5>
                                    </div>
                                    <div class="card-body">
                                        <div class="row">
                                            <div class="col-lg-6">
                                                <ValidationProvider vid="selection" name="Malltyp" rules="" v-slot="{ errors }">
                                                    <div class="form-group">
                                                        <label class="d-block">Malltyp</label>
                                                        <div class="custom-control custom-radio custom-control-inline">
                                                            <input type="radio" class="custom-control-input" name="use_view" id="rdo-use-view" v-model="settings.use_view" :value="1">
                                                            <label class="custom-control-label" for="rdo-use-view">Rednax™ Builder</label>
                                                        </div>
                                                        <div class="custom-control custom-radio custom-control-inline">
                                                            <input type="radio" class="custom-control-input" name="use_view" id="rdo-use-legacy" v-model="settings.use_view" :value="0">
                                                            <label class="custom-control-label" for="rdo-use-legacy">Legacy</label>
                                                        </div>
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </div>
                                                </ValidationProvider>
                                                <input-with-validation
                                                    vid="static_variable"
                                                    label="Ämne"
                                                    v-model="settings.static_variable"
                                                    placeholder="##StaticVariable##"
                                                    rules="required"
                                                ></input-with-validation>
                                                <simple-text-editor
                                                    cardheader="Texthälsning"
                                                    v-model="settings.text"
                                                    :toolbar="[
                                                        'h1',
                                                        'h2',
                                                        'h3',
                                                        'bold',
                                                        'italic',
                                                        'strike',
                                                        'underline',
                                                        'textalign',
                                                    ]"
                                                ></simple-text-editor>
                                            </div>
                                            <div class="col-lg-6">
                                                <select-with-validation
                                                    vid="ecard_template_id"
                                                    label="Epostmall"
                                                    component="ecard-template-filter"
                                                    v-model="settings.ecard_template_id"
                                                    :reduce="(opt) => opt.id"
                                                    placeholder="Välj mall..."
                                                    rules="required"
                                                    :customer-id="settings.customer_id"
                                                ></select-with-validation>
                                                <div class="form-group">
                                                    <label>Logo/Bild 1</label>
                                                    <div v-if="settings.image_slot_1">
                                                        <img v-if="settings.image_slot_1_url" :src="settings.image_slot_1_url" width="100px" class="mb-2" alt="Bild 1">
                                                        <div class="input-group">
                                                            <input type="text" class="form-control" :value="settings.image_slot_1" disabled>
                                                            <div class="input-group-append">
                                                                <button type="button" class="btn btn-danger" @click.prevent="removeImage(1)"><i class="icon-trash"></i></button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <ValidationProvider v-else vid="image_slot_1_file" name="Logo/Bild 1" rules="" v-slot="{ errors }">
                                                        <limitless-file-input @change="(files) => { settings.image_slot_1_file = (files[0] ? files[0] : null); }" ref="fileInput1"></limitless-file-input>
                                                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                                <div class="form-group">
                                                    <label>Logo/Bild 2</label>
                                                    <div v-if="settings.image_slot_2">
                                                        <img v-if="settings.image_slot_2_url" :src="settings.image_slot_2_url" width="100px" class="mb-2" alt="Bild 2">
                                                        <div class="input-group">
                                                            <input type="text" class="form-control" :value="settings.image_slot_2" disabled>
                                                            <div class="input-group-append">
                                                                <button type="button" class="btn btn-danger" @click.prevent="removeImage(2)"><i class="icon-trash"></i></button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <ValidationProvider v-else vid="image_slot_2_file" name="Logo/Bild 2" rules="" v-slot="{ errors }">
                                                        <limitless-file-input @change="(files) => { settings.image_slot_2_file = (files[0] ? files[0] : null); }" ref="fileInput2"></limitless-file-input>
                                                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                                <div class="custom-control custom-checkbox mb-2" >
                                                    <input type="checkbox" class="custom-control-input" id="chk-automatic_sendouts" v-model="settings.embed_images" :true-value="1" :false-value="0">
                                                    <label id="embed-images" class="custom-control-label" for="chk-automatic_sendouts">Aktivera inbakning av bilder (CID)</label>
                                                </div>
                                                <b-tooltip target="embed-images" placement="bottomright">
                                                    Bifogar bilderna direkt i mailet. OBS! Stänger av tracking!
                                                </b-tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </template>
                    </limitless-tabs>
                </ValidationObserver>
            </div>

            <b-modal
                id="modal-send-test"
                :title="testChannel === 'sms' ? 'Skicka test-SMS' : 'Skicka test via e-post'"
                :no-close-on-backdrop="busyTestSend"
                :no-close-on-esc="busyTestSend"
                :hide-header-close="busyTestSend"
                @ok="sendTest"
            >
                <p>Testet använder den sparade mallen och exempelmottagaren Ture Testare med ett påhittat kortnummer.</p>
                <ValidationObserver ref="testform">
                    <form @submit.prevent="sendTest">
                        <input-with-validation
                            :key="testChannel"
                            vid="to"
                            :label="testChannel === 'sms' ? 'Mobilnummer' : 'E-postadress'"
                            v-model="testDestinations[testChannel]"
                            :rules="testChannel === 'sms' ? 'required' : 'required|email'"
                            :disabled="busyTestSend"
                        />
                    </form>
                </ValidationObserver>
                <p v-if="testSendError" class="text-danger mb-0" role="alert">{{ testSendError }}</p>
                <template #modal-footer="{ cancel, ok }">
                    <button type="button" class="btn btn-light" @click="cancel" :disabled="busyTestSend">Avbryt</button>
                    <limitless-button type="button" @click.prevent="ok" :busy="busyTestSend" :disabled="sendBlocked">Skicka test</limitless-button>
                </template>
            </b-modal>

            <b-modal title="Mottagardetaljer" id="modal-recipient-details" size="lg" ok-only ok-title="Stäng" ok-variant="light">
                <div v-if="recipientDetails">
                    <p>
                        <span :class="'badge badge-' + recipientStatus(recipientDetails).variant">{{ recipientStatus(recipientDetails).label }}</span>
                        <span v-if="Number(recipientDetails.is_redeemed)" class="badge badge-success ml-1">Inlöst</span>
                        <span v-if="Number(recipientDetails.test_card_mode)" class="badge badge-warning ml-1">Testläge</span>
                    </p>
                    <dl class="row mb-0">
                        <template v-for="entry in recipientDetailRows">
                            <dt :key="entry.label + '-label'" class="col-sm-4">{{ entry.label }}</dt>
                            <dd :key="entry.label + '-value'" class="col-sm-8" style="overflow-wrap: anywhere;">{{ entry.value }}</dd>
                        </template>
                    </dl>
                    <p v-if="recipientDetails.delivery_channel === 'sms'" class="text-muted small mb-0">
                        Skickat innebär att SMS-tjänsten har tagit emot meddelandet. Levererad visas först när en leveransrapport har registrerats.
                    </p>
                </div>
            </b-modal>

            <b-modal title="Lägg till mottagare" id="modal-add-recipients" size="xl" ok-only ok-title="Stäng" ok-variant="light">
                <csv-exporter
                    v-if="settings"
                    @csv:exported="$refs.recipientstable.reload()"
                    :card-order-id="id"
                    :card-series-ids="settings.card_series_ids"
                />
            </b-modal>

            <b-modal title="Schemalägg/Ange utskicksdatum" id="modal-edit-date" @ok="massAssignEmailDate">
                <div class="alert alert-info alert-styled-left">
                    Utskicket schemaläggs för mottagare som inte redan har löst in sitt kort och vars leveranssätt har en konfigurerad mall.
                    Välj "Radera datum" om du istället vill stoppa ett redan schemalagt utskick.
                </div>
                <ValidationObserver ref="dateform" v-if="settings">
                    <form @submit="massAssignEmailDate">
                        <ValidationProvider vid="scheduled_ts" :rules="date.delete_date ? '' : 'required'" v-slot="{ errors }">
                            <div class="form-group">
                                <label>Utskicksdatum</label>
                                <div class="custom-control custom-radio mb-2">
                                    <input type="radio" class="custom-control-input" name="date-set" id="rdo-date-set" v-model="date.delete_date" :value="false" :disabled="!hasConfiguredChannel">
                                    <label class="custom-control-label" for="rdo-date-set">Ange nytt datum</label>
                                </div>
                                <div class="custom-control custom-radio mb-2">
                                    <input type="radio" class="custom-control-input" name="date-set" id="rdo-date-delete" v-model="date.delete_date" :value="true">
                                    <label class="custom-control-label" for="rdo-date-delete">Radera datum</label>
                                </div>
                                <div v-if="!date.delete_date">
                                    <v-date-picker
                                        mode="dateTime"
                                        is24hr
                                        v-model="date.scheduled_ts"
                                        :model-config="{ type: 'string', mask: 'iso', timezones: ['Europe/Stockholm'], timezone: 'Europe/Stockholm', locale: 'sv-SE' }"
                                        :min-date="new Date()"
                                    ></v-date-picker>
                                </div>
                                <span class="validation-invalid-label">{{ errors[0] }}</span>
                            </div>
                        </ValidationProvider>

                        <select-with-validation
                            label="Urval (kortserie)"
                            placeholder="Ange för alla mottagare oavsätt kortserie"
                            v-model="date.card_series_id"
                            :options="settings.card_series_ids"
                            rules=""
                        ></select-with-validation>
                    </form>
                </ValidationObserver>
                <template #modal-footer="{ cancel, ok }">
                    <button type="button" class="btn btn-light" @click="cancel">Avbryt</button>
                    <limitless-button ref="btndateform" type="button" @click.prevent="ok" :busy="busy_date" :disabled="scheduleBlocked">Spara</limitless-button>
                </template>
            </b-modal>

            <b-modal id="modal-recipient-preview" :title="preview && preview.delivery_channel === 'sms' ? 'Visa SMS – aktuell text' : 'Visa sparad e-post'" size="lg" ok-only ok-title="Stäng" ok-variant="light">
                <div v-if="preview">
                    <div v-if="preview.delivery_channel === 'sms'">
                        <p>Till: {{ preview.to }}</p>
                        <pre class="border p-3" style="white-space: pre-wrap; overflow-wrap: anywhere;">{{ preview.text }}</pre>
                        <sms-summary :text="preview.text" />
                    </div>
                    <template v-else>
                        <p>
                            Ämne: {{ preview.subject }} <br>
                            Från: {{ preview.from }} <br>
                            Till: {{ preview.to }} <br>
                        </p>
                        <b-card no-body style="padding: 0;">
                            <Iframe
                                frameborder="0"
                                style="width: 100%; height: 70vh; overflow-x: hidden;">
                                <div v-html="preview.html"></div>
                            </Iframe>
                        </b-card>
                    </template>
                </div>
                <div v-else><i class="text-danger">Kan ej förhandsgranska</i></div>
            </b-modal>

            <b-modal id="modal-recipient-edit" size="lg" @ok="saveRecipient">
                <ValidationObserver ref="recipientform" v-if="recipient">
                    <form @submit.prevent="saveRecipient">
                        <div class="form-group">
                            <label for="recipient-delivery-channel">Leveranssätt</label>
                            <select id="recipient-delivery-channel" class="form-control" v-model="recipient.delivery_channel">
                                <option value="email">E-post</option>
                                <option value="sms">SMS</option>
                            </select>
                        </div>
                        <input-with-validation
                            vid="recipient_mobile"
                            label="Mobilnummer"
                            placeholder="0701234567 eller +46701234567"
                            v-model="recipient.recipient_mobile"
                            :rules="recipient.delivery_channel === 'sms' ? 'required' : ''"
                        ></input-with-validation>
                        <input-with-validation
                            vid="recipient_email"
                            label="E-post"
                            v-model="recipient.recipient_email"
                            :rules="recipient.delivery_channel === 'email' ? 'required|email' : 'email'"
                        ></input-with-validation>
                    </form>
                </ValidationObserver>
                <template #modal-footer="{ cancel, ok }">
                    <limitless-button
                        ref="btndeleterecipient"
                        type="button"
                        btn-class="btn-danger"
                        icon="icon-trash"
                        @click="deleteRecipient"
                        :busy="busy_delete_recipient"
                        class="mr-auto"
                    >
                        Radera
                    </limitless-button>
                    <button
                        type="button"
                        class="btn btn-light"
                        @click="cancel"
                    >
                        Avbryt
                    </button>
                    <limitless-button
                        ref="btnrecipientform"
                        type="button"
                        @click.prevent="ok"
                        :busy="busy_recipient"
                    >
                        Spara
                    </limitless-button>
                </template>
            </b-modal>
        </div>
    </div>
</template>

<script>
import TemplateEditor from '@components/Ecard/TemplateEditor.vue';
import CsvExporter from '@app/pages/cardorder/partials/CsvExporter.vue';
import apiClient from '@services/ApiClient';
import SimpleTextEditor from '@components/SimpleTextEditor.vue';
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader.vue';
import LimitlessButton from '@components/Limitless/LimitlessButton.vue';
import SelectWithValidation from '@components/Form/SelectWithValidation.vue';
import getError from '@services/ErrorHelper';
import InputWithValidation from '@components/Form/InputWithValidation.vue';
import LimitlessFileInput from '@components/Limitless/LimitlessFileInputComponent.vue';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent.vue';
import VueSelect from '@components/Wrappers/VueSelect.vue';
import { formatDateTime } from '@services/Helpers';
import Iframe from '@components/Iframe.vue';
import SmsSummary from '@components/Ecard/SmsSummary.vue';
import RecipientEvent from '@components/Ecard/RecipientEvent.vue';
import LimitlessTabs from '@components/Limitless/LimitlessTabs.vue';
import { debounce } from 'lodash';

const editableSettings = [
    'sms_text', 'use_view', 'subject', 'sender_name', 'view', 'text', 'ecard_template_id',
    'static_variable', 'embed_images', 'image_slot_1', 'image_slot_2', 'image_slot_1_file', 'image_slot_2_file',
];

function canonical(value) {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === 'object') {
        return Object.keys(value).sort().reduce((result, key) => {
            result[key] = canonical(value[key]);
            return result;
        }, {});
    }
    return value;
}

function settingValue(settings, key) {
    const value = settings?.[key];
    if (key.endsWith('_file')) return value || null;
    if (['use_view', 'embed_images'].includes(key)) return Number(value || 0);
    if (key === 'view') return JSON.stringify(canonical(value || null));
    return String(value ?? '');
}

function settingsSnapshot(settings) {
    return Object.fromEntries(editableSettings.map((key) => [key, settingValue(settings, key)]));
}

function sameSettings(first, second) {
    return editableSettings.every((key) => first[key] === second[key]);
}

// Apply server normalization only to fields that have not changed since submission.
function mergeSavedSettings(current, submittedSnapshot, saved) {
    const currentSnapshot = settingsSnapshot(current);
    const result = { image_slot_1_file: null, image_slot_2_file: null, ...saved };
    editableSettings.forEach((key) => {
        if (currentSnapshot[key] !== submittedSnapshot[key]) result[key] = current[key];
    });
    return result;
}

function recipientContact(row) {
    return (row.delivery_channel === 'sms' ? row.recipient_mobile : row.recipient_email) || '';
}

function recipientName(row) {
    return [row.recipient_firstname, row.recipient_lastname].filter(Boolean).join(' ').trim()
        || recipientContact(row) || row.card_uid;
}

function recipientStatus(row) {
    if (Number(row.email_scheduled)) return { label: 'Schemalagt', variant: 'info' };
    if (row.delivery_channel === 'sms') {
        if (row.sms_failed_ts) return { label: 'Leveransfel', variant: 'danger' };
        if (row.sms_delivered_ts) return { label: 'Levererad', variant: 'success' };
    } else {
        if (row.queued_ts && (!row.email_last_sent_ts || row.queued_ts > row.email_last_sent_ts)
            && (!row.bounced_ts || row.queued_ts > row.bounced_ts)) {
            return { label: 'Köat för e-post', variant: 'info' };
        }
        if (row.bounced_ts) return { label: 'Leveransfel', variant: 'danger' };
        if (row.delivered_ts && (!row.email_last_sent_ts || row.delivered_ts >= row.email_last_sent_ts)) {
            return { label: 'Levererad', variant: 'success' };
        }
    }
    const sent = row.delivery_channel === 'sms' ? row.sms_sent_ts : row.email_sent_ts;
    return sent ? { label: 'Skickat', variant: 'info' } : { label: 'Ej skickat', variant: 'secondary' };
}

function sendLabel(row) {
    const channel = row.delivery_channel === 'sms' ? 'SMS' : 'e-post';
    const sent = row.delivery_channel === 'sms' ? row.sms_sent_ts : row.email_sent_ts;
    return `Skicka ${channel}${sent ? ' igen' : ''}`;
}

export default {
    name: 'CardOrderEcards',
    components: {
        LimitlessTabs,
        RecipientEvent,
        SmsSummary,
        Iframe,
        VueSelect,
        RednaxDatatable,
        LimitlessFileInput,
        InputWithValidation,
        SelectWithValidation,
        LimitlessButton,
        SimpleTextEditor,
        CsvExporter,
        VDatePicker,
        LimitlessPageHeader,
        TemplateEditor,
    },
    props: {
        id: {
            type: [Number, String],
            default: 0,
        },
    },
    data() {
        return {
            smsVariables: ['CardNo', 'GreetingText', 'GreetingImage', 'GreetingLogo', 'GreetingColor', 'FirstName', 'LastName', 'FullName', 'Email', 'Mobile', 'Company', 'Subject', 'StaticVariable',
                'SenderName', 'CompanyName', 'ImageSlot1', 'ImageSlot2', 'ImageSlotElement1', 'ImageSlotElement2',
                ...Array.from({ length: 10 }, (_, i) => 'Variable_' + String(i + 1).padStart(2, '0'))],
            busy_loading: false,
            busy_saving: false,
            busy_recipient: false,
            busy_delete_recipient: false,
            busy_date: false,
            activeTab: 1,
            tabs: [
                {
                    label: 'Mottagare', slot: 'recipients', icon_before: 'icon-users', negative_margin: true,
                },
                {
                    label: 'SMS', slot: 'sms', icon_before: 'icon-mobile', negative_margin: true,
                },
                {
                    label: 'E-postmall', slot: 'email', icon_before: 'icon-envelop',
                },
            ],
            recipientEventColumns: ['sent_ts', 'delivered_ts', 'opened_ts', 'clicked_ts', 'is_redeemed', 'bounced_ts'],
            savedSettingsSnapshot: null,
            saveError: '',
            sendingRecipientId: null,
            testChannel: 'sms',
            testDestinations: { email: '', sms: '' },
            busyTestSend: false,
            testSendError: '',
            recipientDetails: null,
            settings: null,
            preview: null,
            smsPreview: null,
            busySmsRecipient: false,
            smsPreviewError: '',
            busySmsPreview: false,
            smsPreviewVersion: 0,
            recipient: null,
            table_filter: {
                search: '',
                card_order_id: null,
                card_series_id: null,
                is_redeemed: null,
            },
            table_columns: [
                { header: 'Mottagare', data: 'recipient_lastname', sortable: true },
                { header: 'Leveranssätt', data: 'delivery_channel', sortable: true },
                { header: 'Status', data: 'is_sent', sortable: true },
                {
                    header: 'Skickat', data: 'sent_ts', sortable: true, class: 'text-center',
                },
                {
                    header: 'Levererad', data: 'delivered_ts', sortable: true, class: 'text-center',
                },
                {
                    header: 'Öppnad', data: 'opened_ts', sortable: true, class: 'text-center',
                },
                {
                    header: 'Klickat', data: 'clicked_ts', sortable: true, class: 'text-center',
                },
                {
                    header: 'Inlöst', data: 'is_redeemed', sortable: true, class: 'text-center',
                },
                {
                    header: 'Bounce', data: 'bounced_ts', sortable: true, class: 'text-center',
                },
                { header: 'Senaste utskick', data: 'last_sent_ts', sortable: true },
                { header: 'Utskicksdatum', data: 'scheduled_ts', sortable: true },
                { header: 'Åtgärder', data: 'buttons', sortable: false },
            ],
            date: {
                card_series_id: null,
                scheduled_ts: null,
                delete_date: false,
            },
        };
    },
    computed: {
        hasConfiguredChannel() {
            return Object.values(this.settings?.configured_channels || {}).some(Boolean);
        },
        missingTemplateMessage() {
            const channels = this.settings?.configured_channels || {};
            const missing = [['email', 'e-post'], ['sms', 'SMS']].filter(([channel]) => !channels[channel]).map(([, label]) => label);
            return missing.length ? `Mall saknas för ${missing.join(' och ')}. Manuella utskick och schemaläggning är inaktiverade för dessa leveranssätt.` : '';
        },
        scheduleBlocked() {
            return this.sendBlocked || (!this.date.delete_date && !this.hasConfiguredChannel);
        },
        recipientDetailRows() {
            const row = this.recipientDetails;
            if (!row) return [];
            const sms = row.delivery_channel === 'sms';
            const date = (value) => (value ? formatDateTime(value) : '–');
            const entries = [
                { label: 'Mottagare', value: recipientName(row) },
                { label: 'Leveranssätt', value: sms ? 'SMS' : 'E-post' },
                { label: 'E-post', value: row.recipient_email || '–' },
                { label: 'Mobilnummer', value: row.recipient_mobile || '–' },
                { label: 'Kortnummer', value: row.card_uid },
                { label: 'Kortserie', value: row.card_series_id },
                { label: 'Första utskick', value: date(sms ? row.sms_sent_ts : row.email_sent_ts) },
                { label: 'Senaste utskick', value: date(sms ? row.sms_last_sent_ts : row.email_last_sent_ts) },
                { label: 'Utskicksdatum', value: date(row.scheduled_ts) },
                { label: 'Schemalagt', value: Number(row.email_scheduled) ? 'Ja' : 'Nej' },
                { label: 'Inlöst', value: Number(row.is_redeemed) ? 'Ja' : 'Nej' },
                { label: 'Testläge', value: Number(row.test_card_mode) ? 'Ja' : 'Nej' },
            ];
            if (sms) {
                entries.push(
                    { label: 'SMS-id', value: row.sms_sent_id || '–' },
                    { label: 'Levererad', value: date(row.sms_delivered_ts) },
                    { label: 'Leveransfel', value: date(row.sms_failed_ts) },
                    { label: 'Felmeddelande', value: row.sms_failed_msg || '–' },
                );
            } else {
                entries.push(
                    { label: 'Köad för e-post', value: date(row.queued_ts) },
                    { label: 'Levererad', value: date(row.delivered_ts) },
                    { label: 'Öppnad', value: date(row.opened_ts) },
                    { label: 'Klickad', value: date(row.clicked_ts) },
                    { label: 'Avregistrerad', value: date(row.unsubscribed_ts) },
                    { label: 'Leveransfel', value: date(row.bounced_ts) },
                    { label: 'Felmeddelande', value: row.bounced_msg || '–' },
                );
            }
            return entries;
        },
        hasUnsavedChanges() {
            return !!this.settings && !!this.savedSettingsSnapshot
                && !sameSettings(settingsSnapshot(this.settings), this.savedSettingsSnapshot);
        },
        sendBlocked() {
            return this.hasUnsavedChanges || this.busy_saving;
        },
        smsPreviewInput() {
            if (!this.settings) return null;
            return {
                delivery_channel: 'sms',
                card_order_id: this.id,
                sms_text: this.settings.sms_text || '',
                use_view: this.settings.use_view,
                text: this.settings.text,
                subject: this.settings.subject,
                sender_name: this.settings.sender_name,
                static_variable: this.settings.static_variable,
            };
        },
    },
    watch: {
        smsPreviewInput: {
            deep: true,
            handler() {
                this.smsPreviewVersion += 1;
                this.smsPreview = null;
                if (this.smsPreviewInput) {
                    this.busySmsPreview = true;
                    this.refreshSmsPreview();
                }
            },
        },
    },
    created() {
        this.refreshSmsPreview = debounce(() => this.fetchSmsPreview(), 350);
    },
    beforeDestroy() {
        this.refreshSmsPreview.cancel();
        this.smsPreviewVersion += 1;
    },
    mounted() {
        this.table_filter.card_order_id = this.id;
        this.getTemplateSettings();
    },
    methods: {
        formatDateTime,
        recipientName,
        recipientContact,
        recipientStatus,
        sendLabel,
        channelConfigured(recipient) {
            return !!this.settings?.configured_channels?.[recipient.delivery_channel || 'email'];
        },
        openTestSend(channel) {
            if (this.sendBlocked || !this.channelConfigured({ delivery_channel: channel }) || this.busyTestSend) return;
            this.testChannel = channel;
            this.testSendError = '';
            this.$bvModal.show('modal-send-test');
            this.$nextTick(() => this.$refs.testform?.reset());
        },
        async sendTest(event) {
            event.preventDefault();
            if (this.sendBlocked || this.busyTestSend || !this.channelConfigured({ delivery_channel: this.testChannel })) return;
            this.busyTestSend = true;
            this.testSendError = '';
            try {
                const valid = await this.$refs.testform.validate();
                if (!valid || this.sendBlocked) return;
                await apiClient.post('/vue/ecard/recipients/send-test', {
                    card_order_id: this.id,
                    delivery_channel: this.testChannel,
                    to: this.testDestinations[this.testChannel],
                });
                this.$bvModal.hide('modal-send-test');
                this.$swal({ title: 'Testutskicket har skickats!', icon: 'success', timer: 3000 });
            } catch (error) {
                const parsed = getError(error);
                if (parsed && typeof parsed === 'object') this.$refs.testform.setErrors(parsed);
                this.testSendError = error.response?.data?.message || 'Testutskicket kunde inte skickas. Försök igen.';
            } finally {
                this.busyTestSend = false;
            }
        },
        showRecipientDetails(recipient) {
            this.recipientDetails = { ...recipient };
            this.$bvModal.show('modal-recipient-details');
        },
        async previewRecipientSms(recipient) {
            this.busySmsRecipient = true;
            try {
                const response = await apiClient.post(`/vue/ecard/recipients/${recipient.recipient_id}/preview`, this.smsPreviewInput);
                this.preview = response.data.data;
                this.$bvModal.show('modal-recipient-preview');
            } catch (error) {
                this.$swal({ title: 'Kunde inte förhandsgranska SMS', text: 'Kontrollera texten och försök igen.', icon: 'error' });
            } finally {
                this.busySmsRecipient = false;
            }
        },
        async fetchSmsPreview() {
            if (!this.smsPreviewInput) return;
            const version = this.smsPreviewVersion + 1;
            this.smsPreviewVersion = version;
            this.smsPreview = null;
            this.smsPreviewError = '';
            this.busySmsPreview = true;
            try {
                const response = await apiClient.post('/vue/ecard/recipients/0/preview', this.smsPreviewInput);
                if (version !== this.smsPreviewVersion) return;
                this.smsPreview = response.data.data;
            } catch (error) {
                if (version === this.smsPreviewVersion) {
                    this.smsPreviewError = 'Kunde inte förhandsgranska SMS. Kontrollera texten och försök igen.';
                }
            } finally {
                if (version === this.smsPreviewVersion) this.busySmsPreview = false;
            }
        },
        insertSmsText(text) {
            if (!text) return;
            const input = this.$refs.smsText;
            const value = this.settings.sms_text || '';
            const start = input.selectionStart;
            const end = input.selectionEnd;
            this.settings.sms_text = value.slice(0, start) + text + value.slice(end);
            this.$nextTick(() => {
                input.focus();
                input.setSelectionRange(start + text.length, start + text.length);
            });
        },
        getTemplateSettings() {
            this.busy_loading = true;
            apiClient.get('/vue/cardorders/' + this.id + '/ecard_settings')
                .then((response) => {
                    this.settings = { ...response.data, image_slot_1_file: null, image_slot_2_file: null };
                    this.testDestinations = { email: '', sms: '', ...response.data.test_recipient_defaults };
                    this.savedSettingsSnapshot = settingsSnapshot(response.data);
                })
                .finally(() => {
                    this.busy_loading = false;
                });
        },
        async saveTemplateSettings() {
            if (this.busy_saving || !this.hasUnsavedChanges) return;
            this.busy_saving = true;
            this.saveError = '';
            try {
                const isValid = await this.$refs.settingsform.validate();
                if (!isValid) {
                    this.showSettingsErrors(this.$refs.settingsform.errors);
                    return;
                }
                const submitted = { ...this.settings, view: this.settings.view ? JSON.parse(JSON.stringify(this.settings.view)) : null };
                const submittedSnapshot = settingsSnapshot(submitted);
                let formData = submitted;
                if (!Number(submitted.use_view)) {
                    formData = new FormData();
                    editableSettings.forEach((key) => {
                        if (key !== 'view' && submitted[key] !== undefined) formData.append(key, submitted[key] ?? '');
                    });
                }
                const response = await apiClient.post('/vue/cardorders/' + this.id + '/ecard_settings', formData);
                this.settings = mergeSavedSettings(this.settings, submittedSnapshot, response.data);
                this.savedSettingsSnapshot = settingsSnapshot(response.data);
                this.$refs.btnsavesettings.triggerSaved();
            } catch (error) {
                const parsederror = getError(error);
                if (parsederror && typeof parsederror === 'object') {
                    this.$refs.settingsform.setErrors(parsederror);
                    this.showSettingsErrors(parsederror);
                } else {
                    this.saveError = 'Kunde inte spara ändringarna. Försök igen.';
                }
                this.$refs.btnsavesettings.triggerFailed();
            } finally {
                this.busy_saving = false;
            }
        },
        showSettingsErrors(errors = {}) {
            const fields = Object.keys(errors).filter((key) => errors[key]?.length);
            this.activeTab = fields.some((key) => key !== 'sms_text') ? 3 : 2;
            this.saveError = 'Kontrollera de markerade fälten och spara igen.';
            this.$nextTick(() => {
                const panel = this.$el.querySelector('.tab-pane.active');
                panel?.querySelector('input[aria-invalid="true"], textarea, input, select')?.focus();
            });
        },
        async removeImage(id) {
            let doChange = false;
            await this.$swal({
                text: 'Är du säker att du vill ta bort bilden?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ja, ta bort!',
                cancelButtonText: 'Avbryt',
            }).then((result) => {
                if (result.isConfirmed) {
                    doChange = true;
                }
            });

            if (doChange) {
                this.$set(this.settings, 'image_slot_' + id, '');
            }
        },
        async massAssignEmailDate(event) {
            event.preventDefault();
            if (this.scheduleBlocked || this.busy_date) return;
            const isValid = await this.$refs.dateform.validate();
            if (this.scheduleBlocked || this.busy_date) return;
            if (isValid) {
                const options = {
                    timeZone: 'Europe/Stockholm',
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    second: 'numeric',
                };
                const formatter = new Intl.DateTimeFormat('sv-SE', options);
                const formatted = this.date.delete_date ? null : formatter.format(new Date(this.date.scheduled_ts));

                this.busy_date = true;
                apiClient.post('/vue/ecard/recipients/mass-assign', {
                    card_order_id: this.id,
                    card_series_id: this.date.card_series_id,
                    scheduled_ts: formatted,
                }).then(() => {
                    this.$refs.btndateform.triggerSaved();
                    this.$bvModal.hide('modal-edit-date');
                    this.$refs.recipientstable.reload();
                }).catch((error) => {
                    const parsederror = getError(error);
                    if (typeof parsederror === 'object') {
                        this.$refs.dateform.setErrors(parsederror);
                    }
                    this.$refs.btndateform.triggerFailed();
                }).finally(() => {
                    this.busy_date = false;
                });
            } else {
                this.$refs.btndateform.triggerFailed();
            }
        },
        previewRecipient(recipient) {
            apiClient.post(`/vue/ecard/recipients/${recipient.recipient_id}/preview`, { delivery_channel: 'email' }).then((res) => {
                this.preview = res.data.data;
                this.$bvModal.show('modal-recipient-preview');
            }).catch(() => {
                this.$swal({ title: 'Kunde inte visa e-post', text: 'Försök igen.', icon: 'error' });
            });
        },
        editRecipient(recipient) {
            this.recipient = { ...recipient, delivery_channel: recipient.delivery_channel || 'email' };
            this.$bvModal.show('modal-recipient-edit');
        },
        async saveRecipient(event) {
            event.preventDefault();
            if (this.recipient) {
                const isValid = await this.$refs.recipientform.validate();
                if (isValid) {
                    this.busy_recipient = true;
                    apiClient.post(`/vue/ecard/recipients/${this.recipient.recipient_id}`, this.recipient).then(() => {
                        this.$refs.btnrecipientform.triggerSaved();
                        this.$bvModal.hide('modal-recipient-edit');
                        this.$refs.recipientstable.reload();
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.recipientform.setErrors(parsederror);
                        }
                        this.$refs.btnrecipientform.triggerFailed();
                    }).finally(() => {
                        this.busy_recipient = false;
                    });
                } else {
                    this.$refs.btnrecipientform.triggerFailed();
                }
            }
        },
        async deleteRecipient() {
            let doDelete = false;
            if (this.recipient) {
                await this.$swal({
                    text: 'Är du säker att du vill radera denna mottagare?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Ja, radera!',
                }).then((result) => {
                    if (result.isConfirmed) {
                        doDelete = true;
                    }
                });
            }
            if (doDelete) {
                this.busy_delete_recipient = true;
                await apiClient.delete(`/vue/ecard/recipients/${this.recipient.recipient_id}`)
                    .then(() => {
                        this.$bvModal.hide('modal-recipient-edit');
                        this.$refs.recipientstable.reload();
                    }).catch(() => {
                        this.$refs.btndeleterecipient.triggerFailed();
                    }).finally(() => {
                        this.busy_delete_recipient = false;
                    });
            }
        },
        sendRecipient(recipient) {
            if (this.sendBlocked || !this.channelConfigured(recipient) || this.sendingRecipientId !== null) return;
            this.sendingRecipientId = recipient.recipient_id;
            apiClient.post(`/vue/ecard/recipients/${recipient.recipient_id}/send`).then(() => {
                this.$swal({
                    title: 'Skickat!',
                    text: 'Utskicket har skickats!',
                    icon: 'success',
                    timer: 3000,
                });
                this.$refs.recipientstable.reload();
            }).catch((error) => {
                let message = 'Utskicket kunde ej skickas.';
                if (error.response && error.response.data && error.response.data.message) {
                    message = error.response.data.message;
                }
                this.$swal({
                    title: 'Något gick fel...',
                    text: message,
                    icon: 'error',
                    timer: 3000,
                });
            }).finally(() => {
                this.sendingRecipientId = null;
            });
        },
        toggleTestmode(recipient) {
            apiClient.post(`/vue/ecard/recipients/${recipient.recipient_id}/set-test-mode`, { test_card_mode: !Number(recipient.test_card_mode) })
                .then(() => {
                    this.$refs.recipientstable.reload();
                });
        },
    },
};
</script>

<style scoped>
.ecard-savebar {
    position: sticky;
    top: 0;
    z-index: 1010;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
.ecard-row-actions {
    gap: 0.35rem;
    min-width: 260px;
}
.ecard-recipients-table ::v-deep .card-header {
    flex-wrap: wrap;
    gap: 0.5rem;
}
.ecard-recipients-table ::v-deep .header-elements {
    flex-wrap: wrap;
    gap: 0.5rem;
}
.ecard-settings-section > .card-body {
    padding: 0;
}
.ecard-settings-section > .card-header,
.ecard-recipients-table ::v-deep > .card-header {
    padding: 0 0 1rem;
}
.ecard-recipients-table ::v-deep > .card-body {
    padding-left: 0;
    padding-right: 0;
}
</style>
